import React from 'react';
import { Asset as ContentfulAsset } from 'contentful';
import cx from 'classnames';
import { getImageData } from '../../../utils/contentful';
import Image, { ImageLoaderProps } from 'next/image';
import { useVideoCache } from '~/utils/videoCache';

interface AssetProps {
  asset: ContentfulAsset;
  videobackups?: ContentfulAsset[];
  videoImageBackup?: ContentfulAsset;
  loading?: 'lazy' | 'eager';
  className?: string;
  muted?: boolean;
}

export const imageLoader = ({ src, width }: ImageLoaderProps) => {
  // Only add https: if the URL doesn't already start with http: or https:
  const url =
    src.startsWith('http://') || src.startsWith('https://')
      ? src
      : `https:${src}`;
  return `${url}?w=${width}&fm=avif`;
};

export const Asset: React.FC<AssetProps> = ({
  asset,
  videobackups = [],
  videoImageBackup = null,
  loading = process.env.NEXT_PUBLIC_ENVIRONMENT === 'percy' ? 'eager' : 'lazy',
  className,
  muted = true
}) => {
  const cachedVideoUrl = useVideoCache(asset?.fields?.file?.url);

  if (!asset) return null;

  const contentType = asset.fields?.file?.contentType ?? '';

  // If it's a video (mp4 or similar), render a video tag
  if (contentType?.includes('video') || contentType?.includes('mp4')) {
    return (
      <div className={cx('', className)}>
        <video
          controls={false}
          title={asset.fields?.title}
          poster={videoImageBackup?.fields?.file?.url}
          disablePictureInPicture
          muted={muted}
          playsInline
          autoPlay
        >
          <source src={cachedVideoUrl} type={contentType} />
          {videobackups.map((backup, index) => (
            <source
              key={index}
              src={
                backup.fields?.file?.url?.startsWith('//')
                  ? `https:${backup.fields.file.url}`
                  : backup.fields.file.url
              }
              type={backup.fields?.file?.contentType}
            />
          ))}
          <track kind="captions" />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  // Otherwise, render as image
  return (
    <div className={cx('', className)}>
      <Image
        className="mx-auto max-w-full"
        loader={imageLoader}
        loading={loading}
        {...getImageData(asset)}
        alt={asset.fields?.description || 'Greenlight image'}
      />
    </div>
  );
};
